import { useState, useEffect, useCallback, useMemo } from 'react'
import { biApi } from '../api/bi'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import { fmtAbrev, fmtNum, PALETA_BI as PALETA, presetRangoBi as presetRango, PRESETS_BI as PRESETS } from '../utils/bi'
import Boton from '../components/Boton'
import MultiSelectFiltro from '../components/MultiSelectFiltro'
import { toISOLocal } from '../utils/fecha'

const iso = (d) => toISOLocal(d)

// Dimensiones con filtro multi-selección (campo en el request ↔ faceta de dimensiones())
// Jerarquía de categorías de Presea (nivel 1 → 6) + otras dimensiones
const MULTI_CAMPOS = [
  { campo: 'grupo', label: 'Grupo', dim: 'grupos' },
  { campo: 'rubro', label: 'Rubro', dim: 'rubros' },
  { campo: 'subrubro', label: 'Subrubro', dim: 'subrubros' },
  { campo: 'tipo_prod', label: 'Tipo prod.', dim: 'tipos_prod' },
  { campo: 'familia', label: 'Familia', dim: 'familias' },
  { campo: 'cepa', label: 'Cepa', dim: 'cepas' },
  { campo: 'tipo_cbte', label: 'Comprobante', dim: 'tipos_cbte' },
  { campo: 'clase', label: 'Clase', dim: 'clases' },
  { campo: 'sucursal', label: 'Sucursal', dim: 'sucursales' },
  { campo: 'bodega', label: 'Bodega', dim: 'bodegas' },
  { campo: 'vendedor', label: 'Vendedor', dim: 'vendedores' },
]
const FILTROS_VACIOS = { grupo: [], rubro: [], subrubro: [], tipo_prod: [], familia: [], cepa: [], tipo_cbte: [], clase: [], sucursal: [], bodega: [], vendedor: [], cliente: '', producto: '' }

// ─── Variación vs período anterior ───────────────────────────────────
function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span style={{ fontSize: text.meta, color: colors.textMeta }}>—</span>
  const up = pct >= 0
  return (
    <span style={{ fontSize: text.sec, fontWeight: 600, color: up ? colors.ok : colors.err, ...sx.mono }}>
      {up ? '▲' : '▼'} {Math.abs(pct).toLocaleString('en-US', { maximumFractionDigits: 1 })}%
    </span>
  )
}

function KpiCard({ label, valor, delta, sub }) {
  return (
    <div style={{ flex: 1, minWidth: 150, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: `${space.lg}px ${space.xl}px` }}>
      <div style={{ ...sx.cardTitle }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: colors.text, ...sx.mono, marginTop: space.xs }}>{valor}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 2 }}>
        <span style={{ fontSize: text.meta, color: colors.textMeta }}>{sub}</span>
        {delta !== undefined && <Delta pct={delta} />}
      </div>
    </div>
  )
}

// ─── Gráfico de líneas/área (tendencia) ──────────────────────────────
function LineaTendencia({ datos }) {
  const W = 920, H = 240, P = { t: 16, r: 16, b: 28, l: 56 }
  if (!datos.length) return <Vacio />
  const max = Math.max(...datos.map((d) => d.facturacion), 1)
  const innerW = W - P.l - P.r, innerH = H - P.t - P.b
  const x = (i) => P.l + (datos.length === 1 ? innerW / 2 : (i / (datos.length - 1)) * innerW)
  const y = (v) => P.t + innerH - (v / max) * innerH
  const linea = datos.map((d, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(d.facturacion).toFixed(1)}`).join(' ')
  const area = `${linea} L ${x(datos.length - 1).toFixed(1)} ${(P.t + innerH).toFixed(1)} L ${x(0).toFixed(1)} ${(P.t + innerH).toFixed(1)} Z`
  const ticks = [0, 0.5, 1].map((f) => ({ v: max * f, y: y(max * f) }))
  const cadaN = Math.ceil(datos.length / 12)

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto' }}>
      {ticks.map((t, i) => (
        <g key={i}>
          <line x1={P.l} x2={W - P.r} y1={t.y} y2={t.y} stroke={colors.borderSoft} />
          <text x={P.l - 8} y={t.y + 4} textAnchor="end" fontSize="10" fill={colors.textMeta} fontFamily={font.mono}>{fmtAbrev(t.v)}</text>
        </g>
      ))}
      <path d={area} fill={colors.link} opacity="0.08" />
      <path d={linea} fill="none" stroke={colors.link} strokeWidth="2" strokeLinejoin="round" />
      {datos.map((d, i) => (i % cadaN === 0 || i === datos.length - 1) && (
        <text key={i} x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill={colors.textMeta} fontFamily={font.mono}>{d.periodo}</text>
      ))}
    </svg>
  )
}

// ─── Dona de categorías (rubro / grupo) ───────────────────────────────
function DonaRubros({ datos, campo = 'rubro' }) {
  const total = datos.reduce((a, d) => a + d.facturacion, 0)
  if (!total) return <Vacio />
  const top = datos.slice(0, 8)
  const otros = datos.slice(8).reduce((a, d) => a + d.facturacion, 0)
  const items = otros > 0 ? [...top, { [campo]: 'Otros', facturacion: otros }] : top
  const R = 70, r = 44, cx = 90, cy = 90
  let ang = -Math.PI / 2
  const arcos = items.map((d, i) => {
    const frac = d.facturacion / total
    const a0 = ang, a1 = ang + frac * 2 * Math.PI
    ang = a1
    const large = a1 - a0 > Math.PI ? 1 : 0
    const p = (rad, a) => [cx + rad * Math.cos(a), cy + rad * Math.sin(a)]
    const [x0, y0] = p(R, a0), [x1, y1] = p(R, a1), [x2, y2] = p(r, a1), [x3, y3] = p(r, a0)
    return { d: `M ${x0} ${y0} A ${R} ${R} 0 ${large} 1 ${x1} ${y1} L ${x2} ${y2} A ${r} ${r} 0 ${large} 0 ${x3} ${y3} Z`, color: PALETA[i % PALETA.length], item: d, frac }
  })
  return (
    <div style={{ display: 'flex', gap: space.xl, alignItems: 'center', flexWrap: 'wrap' }}>
      <svg viewBox="0 0 180 180" style={{ width: 180, height: 180, flexShrink: 0 }}>
        {arcos.map((a, i) => <path key={i} d={a.d} fill={a.color} />)}
      </svg>
      <div style={{ flex: 1, minWidth: 200 }}>
        {arcos.map((a, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: '3px 0', fontSize: text.sec }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: a.color, flexShrink: 0 }} />
            <span style={{ flex: 1, color: colors.text, fontFamily: font.sans, textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(a.item[campo] || '').toLowerCase()}</span>
            <span style={{ ...sx.mono, color: colors.textSec }}>{(a.frac * 100).toFixed(1)}%</span>
            <span style={{ ...sx.mono, color: colors.text, width: 70, textAlign: 'right' }}>{fmtAbrev(a.item.facturacion)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Ranking horizontal (barras) ─────────────────────────────────────
function Ranking({ datos, getLabel, getValor, getSub, color = colors.link }) {
  if (!datos.length) return <Vacio />
  const max = Math.max(...datos.map(getValor), 1)
  return (
    <div>
      {datos.map((d, i) => {
        const v = getValor(d)
        return (
          <div key={i} style={{ padding: '6px 0', borderBottom: `1px solid ${colors.borderSoft}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space.md, marginBottom: 3 }}>
              <span style={{ fontSize: text.body, color: colors.text, fontFamily: font.sans, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                <span style={{ color: colors.textMeta, ...sx.mono, marginRight: 6 }}>{i + 1}</span>{getLabel(d)}
              </span>
              <span style={{ ...sx.mono, fontSize: text.body, fontWeight: 600, color: colors.text, flexShrink: 0 }}>{fmtAbrev(v)}</span>
            </div>
            <div style={{ height: 6, background: colors.borderSoft, borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: `${(v / max) * 100}%`, height: '100%', background: color, borderRadius: 3 }} />
            </div>
            {getSub && <div style={{ fontSize: text.meta, color: colors.textMeta, marginTop: 2 }}>{getSub(d)}</div>}
          </div>
        )
      })}
    </div>
  )
}

function Vacio() {
  return <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta, fontSize: text.sec }}>Sin datos en el período</div>
}

function Card({ titulo, children, ancho }) {
  return (
    <div style={{ flex: ancho || 1, minWidth: 320, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: space.xl }}>
      <h3 style={{ ...sx.cardTitle, marginBottom: space.lg }}>{titulo}</h3>
      {children}
    </div>
  )
}

export default function BiVentasPage() {
  const [preset, setPreset] = useState('anio')
  const [[desde, hasta], setRango] = useState(presetRango('anio'))
  const [fil, setFil] = useState(FILTROS_VACIOS)
  const [cliBuf, setCliBuf] = useState('')
  const [prodBuf, setProdBuf] = useState('')
  const [dims, setDims] = useState({ vendedores: [], tipos_cbte: [], clases: [], rubros: [], subrubros: [], sucursales: [], bodegas: [], rango_datos: {} })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [moneda, setMoneda] = useState('ars')

  useEffect(() => { biApi.dimensiones().then(({ data }) => setDims(data)).catch(() => {}) }, [])

  // Búsqueda de cliente/producto con debounce (no dispara por cada tecla)
  useEffect(() => {
    const t = setTimeout(() => setFil((f) => ({ ...f, cliente: cliBuf, producto: prodBuf })), 400)
    return () => clearTimeout(t)
  }, [cliBuf, prodBuf])

  const cargar = useCallback(() => {
    setLoading(true)
    biApi.ventas({ desde, hasta, moneda, ...fil })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [desde, hasta, moneda, fil])
  useEffect(() => { cargar() }, [cargar])

  const aplicarPreset = (k) => { setPreset(k); setRango(presetRango(k)) }
  const setMulti = (campo) => (vals) => setFil((f) => ({ ...f, [campo]: vals }))
  const limpiarFiltros = () => { setFil(FILTROS_VACIOS); setCliBuf(''); setProdBuf('') }
  const hayFiltros = MULTI_CAMPOS.some((c) => fil[c.campo].length) || fil.cliente || fil.producto
  const k = data?.kpis

  const st = useMemo(() => ({
    input: { padding: '7px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: text.sec, fontFamily: font.sans, color: colors.text, background: colors.bg },
    presetBtn: (on) => ({ ...sx.pill, ...(on ? sx.pillActive : {}) }),
  }), [])

  return (
    <div style={{ padding: space['2xl'], maxWidth: 1240, margin: '0 auto', fontFamily: font.sans }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: space.md, marginBottom: space.lg }}>
        <div>
          <div style={{ fontSize: text.meta, fontWeight: 600, color: colors.textMeta, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Inteligencia comercial</div>
          <h1 style={{ margin: '2px 0 0', fontSize: text.title + 6, color: colors.text }}>Ventas</h1>
        </div>
        {dims.rango_datos?.desde && (
          <div style={{ fontSize: text.meta, color: colors.textMeta, ...sx.mono }}>
            datos: {dims.rango_datos.desde} → {dims.rango_datos.hasta}
          </div>
        )}
      </div>

      {/* Filtros: fecha */}
      <div style={{ display: 'flex', gap: space.md, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.md }}>
        {PRESETS.map((p) => <button key={p.k} style={st.presetBtn(preset === p.k)} onClick={() => aplicarPreset(p.k)}>{p.label}</button>)}
        <span style={{ width: 1, height: 22, background: colors.border }} />
        <input type="date" value={desde} onChange={(e) => { setPreset(''); setRango([e.target.value, hasta]) }} style={st.input} />
        <span style={{ color: colors.textMeta }}>→</span>
        <input type="date" value={hasta} onChange={(e) => { setPreset(''); setRango([desde, e.target.value]) }} style={st.input} />
        <span style={{ width: 1, height: 22, background: colors.border }} />
        <div style={{ display: 'inline-flex', border: `1px solid ${colors.border}`, borderRadius: radius.sm, overflow: 'hidden' }}>
          {[['ars', '$ ARS'], ['usd', 'US$'], ['real', '$ hoy']].map(([m, lbl]) => (
            <button key={m} onClick={() => setMoneda(m)}
              style={{ padding: '7px 12px', border: 'none', cursor: 'pointer', fontSize: text.sec, fontWeight: 600, fontFamily: font.sans, background: moneda === m ? colors.link : colors.bg, color: moneda === m ? '#fff' : colors.textSec }}>
              {lbl}
            </button>
          ))}
        </div>
        {moneda === 'usd' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>convertido al blue de cada día</span>}
        {moneda === 'real' && <span style={{ fontSize: text.meta, color: colors.textMeta }}>pesos constantes de hoy (deflactado por IPC)</span>}
      </div>

      {/* Filtros: multi-selección por dimensión + búsqueda cliente/producto */}
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', flexWrap: 'wrap', marginBottom: space.xl }}>
        {MULTI_CAMPOS.map((m) => (
          <MultiSelectFiltro
            key={m.campo}
            label={m.label}
            options={m.campo === 'vendedor'
              ? (dims.vendedores || []).map((v) => ({ value: v.cod, label: v.nombre }))
              : (dims[m.dim] || [])}
            selected={fil[m.campo]}
            onChange={setMulti(m.campo)}
          />
        ))}
        <input value={cliBuf} onChange={(e) => setCliBuf(e.target.value)} placeholder="Cliente…" style={{ ...st.input, width: 130 }} />
        <input value={prodBuf} onChange={(e) => setProdBuf(e.target.value)} placeholder="Producto / SKU…" style={{ ...st.input, width: 140 }} />
        {hayFiltros && <Boton variante="ghost" sm onClick={limpiarFiltros}>Limpiar todo</Boton>}
      </div>

      {loading && !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.textMeta }}>Cargando…</div>
      ) : !data ? (
        <div style={{ padding: space['3xl'], textAlign: 'center', color: colors.err }}>No se pudieron cargar los datos.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg, opacity: loading ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* KPIs */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <KpiCard label="Facturación" valor={fmtAbrev(k.facturacion)} delta={k.var.facturacion} sub={`con IVA · ${{ ars: 'ARS', usd: 'USD', real: '$ hoy' }[moneda]}`} />
            <KpiCard label="Margen" valor={fmtAbrev(k.margen)} delta={k.var.margen} sub={`${(k.margen_pct ?? 0).toLocaleString('en-US', { maximumFractionDigits: 1 })}% · costo ${fmtAbrev(k.costo)}`} />
            <KpiCard label="Comprobantes" valor={fmtNum(k.comprobantes)} delta={k.var.comprobantes} sub={`ticket ${fmtAbrev(k.ticket_promedio)}`} />
            <KpiCard label="Unidades" valor={fmtNum(k.unidades)} delta={k.var.unidades} sub="vendidas" />
            <KpiCard label="Clientes" valor={fmtNum(k.clientes)} sub="con compras" />
            {data.mes_anterior && (
              <KpiCard label={`Mes anterior · ${data.mes_anterior.etiqueta}`} valor={fmtAbrev(data.mes_anterior.facturacion)}
                delta={data.mes_anterior.var} sub="cerrado · vs mes previo" />
            )}
          </div>

          {/* Tendencia */}
          <Card titulo="Evolución de facturación">
            <LineaTendencia datos={data.tendencia} />
          </Card>

          {/* Grupo (nivel 1) + Rubro (nivel 2) */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <Card titulo="Facturación por grupo"><DonaRubros datos={data.por_grupo} campo="grupo" /></Card>
            <Card titulo="Facturación por rubro"><DonaRubros datos={data.por_rubro} campo="rubro" /></Card>
          </div>

          {/* Vendedores + Clientes */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <Card titulo="Ranking de vendedores">
              <Ranking datos={data.por_vendedor} color={colors.t80Text}
                getLabel={(d) => d.nombre} getValor={(d) => d.facturacion}
                getSub={(d) => `${fmtNum(d.comprobantes)} comprob. · ${fmtNum(d.clientes)} clientes`} />
            </Card>
            <Card titulo="Top clientes">
              <Ranking datos={data.por_cliente}
                getLabel={(d) => d.nombre || `Cliente ${d.cod_cli}`} getValor={(d) => d.facturacion}
                getSub={(d) => `${fmtNum(d.comprobantes)} comprobantes`} />
            </Card>
          </div>

          {/* Productos */}
          <div style={{ display: 'flex', gap: space.lg, flexWrap: 'wrap' }}>
            <Card titulo="Top productos">
              <Ranking datos={data.por_producto} color={PALETA[2]}
                getLabel={(d) => d.descripcion} getValor={(d) => d.facturacion}
                getSub={(d) => `${fmtNum(d.unidades)} u · ${(d.rubro || '').toLowerCase()}`} />
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}
